import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  LogOut, CalendarDays, Users, Inbox, Loader2, Phone, Mail, Car, MapPin,
  CheckCircle2, XCircle, ArrowLeft, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ll_admin_token";
const FIELD = "w-full rounded-xl border border-ink-soft bg-ink-surface px-4 py-3 text-white placeholder:text-white/30 outline-none transition-colors focus:border-gold";

const fmtDate = (ymd) => {
  if (!ymd) return "—";
  const d = new Date(ymd + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};
const fmtCreated = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

export default function Admin() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(null); // null = checking
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [error, setError] = useState("");

  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loadingData, setLoadingData] = useState(false);

  const loadData = useCallback(async (t) => {
    setLoadingData(true);
    try {
      const [s, b] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${t}` } }),
        axios.get(`${API}/bookings`, { headers: { Authorization: `Bearer ${t}` } }),
      ]);
      setStats(s.data);
      setBookings(b.data);
    } catch {
      toast.error("Could not load data.");
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (!token) { setAuthed(false); return; }
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => { setAuthed(true); loadData(token); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setToken(""); setAuthed(false); });
  }, [token, loadData]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setAuthed(true);
      toast.success(`Welcome back, ${data.name}.`);
      loadData(data.token);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setEmail(""); setPassword("");
  };

  if (authed === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink text-white/50">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!authed) {
    return (
      <div className="relative flex min-h-screen items-center justify-center bg-ink grain px-6">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-gold/10 blur-3xl" />
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="relative w-full max-w-md rounded-3xl border border-ink-soft bg-ink-surface p-8 md:p-10"
        >
          <Logo className="text-xl" iconClass="h-5 w-5" />
          <h1 className="mt-8 font-display text-3xl font-medium tracking-tight">Admin Login</h1>
          <p className="mt-2 text-sm text-white/50">Sign in to view bookings and stats.</p>
          <form onSubmit={submit} data-testid="admin-login-form" className="mt-8 space-y-4">
            <input data-testid="admin-email" type="email" className={FIELD} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <input data-testid="admin-password" type="password" className={FIELD} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
            {error && <p data-testid="admin-login-error" className="text-sm text-red-400">{error}</p>}
            <button data-testid="admin-login-submit" type="submit" disabled={loggingIn} className="flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3.5 font-semibold text-ink transition-colors hover:bg-gold-hover disabled:opacity-60">
              {loggingIn ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</> : "Sign In"}
            </button>
          </form>
          <button onClick={() => navigate("/")} className="mt-6 inline-flex items-center gap-1 text-sm text-white/50 hover:text-gold">
            <ArrowLeft className="h-4 w-4" /> Back to site
          </button>
        </motion.div>
      </div>
    );
  }

  const STAT_CARDS = stats ? [
    { icon: Inbox, label: "Total Bookings", value: stats.total_bookings },
    { icon: CalendarDays, label: "Upcoming", value: stats.upcoming_bookings },
    { icon: Users, label: "Subscribers", value: stats.subscribers },
  ] : [];

  return (
    <div className="min-h-screen bg-ink" data-testid="admin-dashboard">
      <header className="sticky top-0 z-20 border-b border-ink-soft bg-ink/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
          <button onClick={() => navigate("/")}><Logo className="text-lg" iconClass="h-5 w-5" /></button>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={() => loadData(token)} className="flex items-center gap-2 rounded-full border border-ink-soft px-4 py-2 text-sm text-white/70 transition-colors hover:border-gold hover:text-gold">
              <RefreshCw className={`h-4 w-4 ${loadingData ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 rounded-full bg-gold px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-gold-hover">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10 md:px-10">
        <h1 className="font-display text-3xl font-medium tracking-tight md:text-4xl">Bookings Dashboard</h1>
        <p className="mt-2 text-white/50">Manage incoming oil-change appointments.</p>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {STAT_CARDS.map((s, i) => (
            <div key={i} data-testid={`admin-stat-${i}`} className="rounded-2xl border border-ink-soft bg-ink-surface p-6">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gold/15 text-gold"><s.icon className="h-5 w-5" strokeWidth={1.5} /></span>
                <span className="text-sm text-white/50">{s.label}</span>
              </div>
              <div className="mt-4 font-display text-4xl font-semibold text-gold">{s.value}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 overflow-hidden rounded-2xl border border-ink-soft">
          <div className="flex items-center justify-between border-b border-ink-soft bg-ink-surface px-6 py-4">
            <h2 className="font-display text-lg font-medium">All Bookings</h2>
            <span className="text-sm text-white/40">{bookings.length} total</span>
          </div>

          {loadingData ? (
            <div className="flex items-center justify-center py-20 text-white/40"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : bookings.length === 0 ? (
            <div className="py-20 text-center text-white/40">No bookings yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" data-testid="admin-bookings-table">
                <thead className="bg-ink-surface text-xs uppercase tracking-wider text-white/40">
                  <tr>
                    <th className="px-6 py-3 font-medium">Appointment</th>
                    <th className="px-6 py-3 font-medium">Customer</th>
                    <th className="px-6 py-3 font-medium">Vehicle / Service</th>
                    <th className="px-6 py-3 font-medium">Address</th>
                    <th className="px-6 py-3 font-medium">Received</th>
                    <th className="px-6 py-3 font-medium">Email</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-soft">
                  {bookings.map((b) => (
                    <tr key={b.id} data-testid="admin-booking-row" className="align-top transition-colors hover:bg-ink-surface/60">
                      <td className="px-6 py-4">
                        <div className="font-medium text-white">{fmtDate(b.booking_date)}</div>
                        <div className="mt-1 text-xs text-gold">{b.slot_label || "—"}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="font-medium text-white">{b.name}</div>
                        <div className="mt-1 flex items-center gap-1 text-xs text-white/50"><Phone className="h-3 w-3" /> {b.phone}</div>
                        <div className="flex items-center gap-1 text-xs text-white/50"><Mail className="h-3 w-3" /> {b.email}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-white/80"><Car className="h-3.5 w-3.5 text-white/40" /> {b.vehicle || "—"}</div>
                        <div className="mt-1 text-xs text-white/50">{b.service || "—"}</div>
                      </td>
                      <td className="px-6 py-4 text-white/70">
                        <div className="flex items-start gap-1"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white/40" /> <span className="max-w-[180px]">{b.address || "—"}</span></div>
                      </td>
                      <td className="px-6 py-4 text-white/50">{fmtCreated(b.created_at)}</td>
                      <td className="px-6 py-4">
                        {b.email_sent
                          ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> Sent</span>
                          : <span className="inline-flex items-center gap-1 text-xs text-white/40"><XCircle className="h-3.5 w-3.5" /> Off</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
